/**
 * Structures a 3D version should default to when it is marked "For CNC": the structures
 * its track is tagged to, once the project has been split. Untagged (whole-project) tracks
 * and unknown ids yield nothing, so the designer still picks by hand in that case.
 */
export function defaultCncStructureIdsForTrack(
  trackStructureIds: readonly string[] | undefined,
  proposalStructureIds: readonly string[],
): string[] {
  const known = new Set(proposalStructureIds);
  const out: string[] = [];
  for (const id of trackStructureIds ?? []) {
    const clean = String(id || '').trim();
    if (clean && known.has(clean) && !out.includes(clean)) out.push(clean);
  }
  return out;
}

/** A 3D version that currently owns one or more structures for CNC. */
export interface CncModelClaim {
  fileId: string;
  fileName: string;
  structureIds: string[];
  structureNames: string[];
}

export interface CncStructureConflict {
  structureId: string;
  structureName: string;
  fileId: string;
  fileName: string;
}

export function cncStructureConflicts(
  claims: readonly CncModelClaim[],
  structureIds: readonly string[],
  structureNames: readonly string[],
): CncStructureConflict[] {
  const nameById = new Map<string, string>();
  structureIds.forEach((id, index) => {
    nameById.set(id, structureNames[index] || id);
  });
  const conflicts: CncStructureConflict[] = [];
  for (const id of structureIds) {
    for (const claim of claims) {
      if (!claim.structureIds.includes(id)) continue;
      conflicts.push({
        structureId: id,
        structureName: nameById.get(id) || claim.structureNames[claim.structureIds.indexOf(id)] || id,
        fileId: claim.fileId,
        fileName: claim.fileName,
      });
    }
  }
  return conflicts;
}

/** Drop structures from a claim. forCnc is false once none remain. */
export function claimAfterDroppingStructures(
  claim: CncModelClaim,
  dropIds: readonly string[],
): CncModelClaim & { forCnc: boolean } {
  const drop = new Set(dropIds);
  const structureIds: string[] = [];
  const structureNames: string[] = [];
  claim.structureIds.forEach((id, index) => {
    if (drop.has(id)) return;
    structureIds.push(id);
    structureNames.push(claim.structureNames[index] || id);
  });
  return {
    fileId: claim.fileId,
    fileName: claim.fileName,
    structureIds,
    structureNames,
    forCnc: structureIds.length > 0,
  };
}
